import { isMapStyleReady } from '../mapcore.js';
// Layer "Temporali e percorso" v2 — port 1:1 del layer chkTemporali v1
// (map-layers.js ~2183-2364): celle temporalesche unificate (fulmini + pioggia
// radar) con percorso previsto (+15/30/60 min). Rendering MapLibre:
// source GeoJSON + circle layer per le celle (paint data-driven da properties
// pre-calcolate, stessi colori/raggi della v1), line layer tratteggiato per le
// traiettorie, circle layer per i punti previsti (con evidenza della posizione
// al lead della timeline). Auto-refresh 2′ con guard document.hidden; timeline
// nowcast condivisa (scrub nel futuro = la cella avanza lungo la traiettoria).
import { NowcastTimeline } from '../ui/nowcast-timeline.js';
import { collectionBaseTime, forecastAtLead, forecastHorizon, mergeNationalCells, parseObservationTime } from '../lib/wsnow-contract.js';

const WS_LIGHTNING_API = '/lightning/api/app.php';
const WS_NATIONAL_API = '/api/wsnow-national.php';
const REFRESH_MS = 60 * 1000;
const MAX_FORECAST_AGE_MS = 20 * 60000;

const SRC_CELLS = 'ws-storms-cells-src';
const SRC_PATHS = 'ws-storms-paths-src';
const SRC_POINTS = 'ws-storms-points-src';
const LYR_CELLS = 'ws-storms-cells';
const LYR_PATHS = 'ws-storms-paths';
const LYR_POINTS = 'ws-storms-points';

// Classificazione tipi (v1 WS_STORM_TYPE_STYLE, valori identici).
const WS_STORM_TYPE_STYLE = {
    developing:   { stroke: '#b45309', fill: '#f59e0b', label: 'Candidato in sviluppo' },
    shower:       { stroke: '#0369a1', fill: '#38bdf8', label: 'Rovescio' },
    electrical:   { stroke: '#1d4ed8', fill: '#3b82f6', label: 'Attività elettrica' },
    thunderstorm: { stroke: '#86198f', fill: '#d946ef', label: 'Temporale' },
};

function stormTypeStyle(type) {
    return WS_STORM_TYPE_STYLE[type] || WS_STORM_TYPE_STYLE.electrical;
}

function publishStormsLegend(on) {
    document.dispatchEvent(new CustomEvent('wsv2:map-legend', {
        detail: {
            id: 'nowcast-storms',
            spec: on ? {
                title: 'WS Now · tracking nazionale',
                unit: 'colore = tipo cella',
                steps: Object.entries(WS_STORM_TYPE_STYLE).map(([, style]) => ({
                    color: style.fill,
                    label: style.label,
                })),
            } : null,
        },
    }));
}

// Raggio per severity (v1 wsStormSeverityRadius, px identici).
function stormSeverityRadius(severity) {
    switch (severity) {
        case 'severe': return 16;
        case 'strong': return 13;
        case 'moderate': return 10;
        default: return 8;
    }
}

// Minuti dall'ultimo segnale della cella (port di wsStormCellAgeMin v1:
// last_seen_at_utc è DATETIME UTC).
function stormCellAgeMin(p) {
    const raw = p && p.last_seen_at_utc;
    if (!raw) return null;
    const t = parseObservationTime(raw);
    if (t === null) return null;
    return Math.max(0, (Date.now() - t) / 60000);
}

const state = {
    map: null,
    enabled: false,
    beforeId: undefined,     // i temporali stanno SOTTO i simboli stazione
    cells: null,             // ultima FeatureCollection storm-cells
    nowcast: null,           // ultima FeatureCollection nowcast (ridisegnata a T)
    leadMin: 0,              // lead corrente della timeline (0 = ora)
    timer: null,
    statusHtml: '',
    _fetching: false,
    _pendingIdle: false,
    baseTimeMs: null,
    generation: 0,
    fetchingGeneration: null,
    nationalStatus: null,
    sources: {},
    _visibilityHandler: null,
    targetTimeMs: null,
};

function esc(value) {
    return String(value ?? '').replace(/[&<>"']/g, c => ({
        '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
    }[c]));
}

function emitStatus(html, details = {}) {
    state.statusHtml = html;
    document.dispatchEvent(new CustomEvent('wsv2:storms-status', { detail: { html, active: state.enabled, ...details } }));
}

// --- layer MapLibre -----------------------------------------------------------

function ensureLayers() {
    const map = state.map;
    if (map.getSource(SRC_CELLS)) return true;
    if (!isMapStyleReady(map)) {
        // Style non ancora pronto (primo load): riprova quando la mappa è idle.
        if (!state._pendingIdle) {
            state._pendingIdle = true;
            map.once('idle', () => {
                state._pendingIdle = false;
                if (state.enabled) { ensureLayers(); renderCells(); drawPath(state.leadMin); }
            });
        }
        return false;
    }
    const empty = { type: 'FeatureCollection', features: [] };
    map.addSource(SRC_PATHS, { type: 'geojson', data: empty });
    map.addSource(SRC_POINTS, { type: 'geojson', data: empty });
    map.addSource(SRC_CELLS, { type: 'geojson', data: empty });

    // Tutto SOTTO i simboli stazione (pattern radar.js); l'ordine di
    // inserimento replica la v1: percorsi sotto, punti previsti, celle sopra.
    const before = map.getLayer('ws-stations-symbols') ? 'ws-stations-symbols' : state.beforeId;
    map.addLayer({
        id: LYR_PATHS, type: 'line', source: SRC_PATHS,
        paint: {
            // v1: dashArray '6 6' con weight 2 → dasharray MapLibre in unità di larghezza.
            'line-color': ['get', 'stroke'],
            'line-width': 2,
            'line-opacity': 0.85,
            'line-dasharray': [3, 3],
        },
    }, before);
    map.addLayer({
        id: LYR_POINTS, type: 'circle', source: SRC_POINTS,
        paint: {
            'circle-radius': ['get', 'r'],
            'circle-color': ['get', 'fill'],
            'circle-opacity': ['get', 'fillOp'],
            'circle-stroke-color': ['get', 'stroke'],
            'circle-stroke-width': ['get', 'sw'],
        },
    }, before);
    map.addLayer({
        id: LYR_CELLS, type: 'circle', source: SRC_CELLS,
        paint: {
            // v1 stormCellMarkerStyle: weight 2 + DISSOLVENZA per età (piena
            // <10' dall'ultimo segnale, sfuma linearmente fino a 45').
            'circle-radius': ['get', 'r'],
            'circle-color': ['get', 'fill'],
            'circle-opacity': ['get', 'fillOp'],
            'circle-stroke-color': ['get', 'stroke'],
            'circle-stroke-width': 2,
            'circle-stroke-opacity': ['get', 'strokeOp'],
        },
    }, before);
    return true;
}

function clearData() {
    const map = state.map;
    if (!map) return;
    const empty = { type: 'FeatureCollection', features: [] };
    for (const id of [SRC_CELLS, SRC_PATHS, SRC_POINTS]) {
        const src = map.getSource(id);
        if (src) src.setData(empty);
    }
}

// --- celle ----------------------------------------------------------------------

function renderCells(T = state.targetTimeMs) {
    const map = state.map;
    if (!map || !map.getSource(SRC_CELLS)) return;
    const feats = (state.cells && state.cells.features) || [];
    // This API is a current snapshot. Past cursors must not reuse today's cells.
    const historical = Number.isFinite(T) && Number.isFinite(state.baseTimeMs) && T < state.baseTimeMs - 5 * 60000;
    const features = (historical ? [] : feats)
        .filter(f => stormCellAgeMin(f.properties || {}) === null || stormCellAgeMin(f.properties || {}) < 45)
        .filter(f => f.geometry && f.geometry.type === 'Point' && Array.isArray(f.geometry.coordinates))
        .map((f, i) => {
            const p = f.properties || {};
            const style = stormTypeStyle(p.storm_type);
            // Dissolvenza per età (v1 2026-07-03): il backend tiene la cella
            // "active" fino a 45-60' senza segnali — fresca <10' = piena, poi
            // sfuma fino al minimo a 45' (k = (45-età)/35 clampato 0..1).
            const age = stormCellAgeMin(p);
            const k = age === null ? 1 : Math.max(0, Math.min(1, (45 - age) / 35));
            return {
                type: 'Feature',
                geometry: f.geometry,
                properties: {
                    ...p,
                    _idx: feats.indexOf(f),
                    fill: style.fill,
                    stroke: style.stroke,
                    r: stormSeverityRadius(p.severity),
                    fillOp: 0.12 + 0.33 * k,
                    strokeOp: 0.35 + 0.65 * k,
                },
            };
        });
    map.getSource(SRC_CELLS).setData({ type: 'FeatureCollection', features });
}

// --- percorso previsto ----------------------------------------------------------
// Port di temporaliDrawPath(leadMin): traiettoria tratteggiata + punti previsti;
// se leadMin>0 evidenzia la POSIZIONE prevista a quel lead (scrubbando nel
// futuro la cella avanza lungo la traiettoria).
function drawPath() {
    const map = state.map;
    if (!map || !map.getSource(SRC_PATHS)) return;
    const lines = [], points = [];
    const target = state.targetTimeMs;
    const nc = state.nowcast;
    if (nc?.type === 'FeatureCollection') {
        for (const f of nc.features || []) {
            const p = f.properties || {};
            const base = parseObservationTime(p.base_time_utc) ?? state.baseTimeMs;
            if (!Number.isFinite(base) || Date.now() - base > MAX_FORECAST_AGE_MS || base > Date.now() + 60000) continue;
            if (Number.isFinite(target) && target < base - 5 * 60000) continue;
            const lead = Number.isFinite(target) ? Math.max(0, (target - base) / 60000) : 0;
            const style = stormTypeStyle(p.storm_type);
            const trajectorySource = p._trajectory_source === 'electrical-tracking' ? 'Tracking della cella elettrica' : 'Radar nazionale DPC';
            const coords = f.geometry?.type === 'LineString' ? f.geometry.coordinates : [];
            const pts = (p.forecast_points || []).filter(pt => pt.prospective !== false && Array.isArray(pt.coordinates)
                && pt.coordinates.slice(0, 2).every(Number.isFinite) && Number(pt.lead_minutes) > 0);
            if (coords.length >= 2 && pts.length) lines.push({
                type: 'Feature', geometry: { type: 'LineString', coordinates: coords },
                properties: { stroke: style.stroke },
            });
            for (const pt of pts) points.push({
                type: 'Feature', geometry: { type: 'Point', coordinates: pt.coordinates },
                properties: { r: 3, sw: 1, fillOp: 0.65, fill: style.fill, stroke: style.stroke,
                    lbl: `Posizione stimata +${pt.lead_minutes} min · ${trajectorySource}${pt.uncertainty_km ? ` · dispersione ±${Math.round(pt.uncertainty_km)} km (non calibrata)` : ''}` },
            });
            const selected = lead > 0 ? forecastAtLead(f, lead) : null;
            if (selected) points.push({
                type: 'Feature', geometry: { type: 'Point', coordinates: selected.coordinates },
                properties: { r: 8, sw: 2, fillOp: 0.95, fill: style.stroke, stroke: '#ffffff',
                    lbl: `Posizione stimata +${Math.round(lead)} min · ${trajectorySource}${selected.uncertainty_km ? ` · dispersione ±${Math.round(selected.uncertainty_km)} km (non calibrata)` : ''}` },
            });
        }
    }
    map.getSource(SRC_PATHS).setData({ type: 'FeatureCollection', features: lines });
    map.getSource(SRC_POINTS).setData({ type: 'FeatureCollection', features: points });
}

function showAtTime(T) {
    state.targetTimeMs = T;
    const base = state.baseTimeMs;
    state.leadMin = Number.isFinite(base) ? Math.max(0, (T - base) / 60000) : 0;
    renderCells(T);
    drawPath();
}

// --- popup celle (port di stormCellPopup) ---------------------------------------

function stormCellPopupHtml(p) {
    const style = stormTypeStyle(p.storm_type);
    const evidence = p.evidence_class || (p.radar_confirmed ? 'radar_confirmed'
        : ((p.has_lightning || p.detection_count) ? 'lightning_observed' : 'unconfirmed_candidate'));
    const displayLabel = p.display_label_it || (evidence === 'radar_confirmed'
        ? 'Cella convettiva confermata dal radar'
        : (evidence === 'lightning_observed'
            ? 'Attività elettrica osservata, non confermata dal radar'
            : 'Cella in sviluppo non confermata dal radar'));
    const rows = [];
    if (p.has_lightning || p.detection_count) {
        rows.push(`<div><dt>Fulmini</dt><dd>${esc(String(p.detection_count || 0))} (rate ${esc(Number(p.flash_rate_per_min || 0).toFixed(1))}/min)</dd></div>`);
    }
    if (p.rain_mm_h_max != null) {
        rows.push(`<div><dt>Pioggia max</dt><dd>${esc(Number(p.rain_mm_h_max).toFixed(1))} mm/h</dd></div>`);
    }
    if (p.motion_speed_kmh != null && p.motion_bearing_deg != null) {
        rows.push(`<div><dt>Moto</dt><dd>${esc(Number(p.motion_speed_kmh).toFixed(0))} km/h verso ${esc(Number(p.motion_bearing_deg).toFixed(0))}°</dd></div>`);
    }
    if (p.dbz != null || p.max_dbz != null || p.reflectivity_max_dbz != null) {
        rows.push(`<div><dt>Eco radar massimo</dt><dd>${esc(Number(p.dbz ?? p.max_dbz ?? p.reflectivity_max_dbz).toFixed(0))} dBZ</dd></div>`);
    }
    if (p.track_confidence || p.motion_confidence || p.confidence_label) {
        const confidence = p.confidence_label || p.track_confidence || p.motion_confidence;
        rows.push(`<div><dt>Qualità del moto</dt><dd>${esc(typeof confidence === 'number' ? 'Indicatore non calibrato' : String(confidence))}</dd></div>`);
    }
    if (p.signal_sources) {
        rows.push(`<div><dt>Fonti</dt><dd>${esc(String(p.signal_sources))}</dd></div>`);
    }
    // Trasparenza sull'età (v1): senza questa riga una cella in dissipazione
    // (fino a 45' senza segnali) sembrava un temporale attivo "adesso".
    const age = stormCellAgeMin(p);
    if (age !== null) {
        const txt = age < 1.5 ? 'adesso' : `${Math.round(age)} min fa`;
        rows.push(`<div><dt>Ultimo segnale</dt><dd>${esc(txt)}${age >= 15 ? ' · dato meno recente' : ''}</dd></div>`);
    }
    return `<article class="ws-storm-popup" style="--ws-storm:${style.fill}">
        <header><strong>${esc(displayLabel)}</strong><span>${esc(({severe:'Eco molto intenso',strong:'Eco intenso',moderate:'Eco moderato',weak:'Segnale debole'})[p.severity] || 'Segnale osservato')}</span></header>
        <dl class="ws-storm-details">${rows.join('')}</dl>
        ${evidence === 'unconfirmed_candidate' ? '<p class="ws-storm-note">Segnale in sviluppo da seguire: non è ancora una cella radar confermata né un temporale osservato.</p>' : ''}
        ${evidence === 'lightning_observed' ? '<p class="ws-storm-note">Attività elettrica osservata; manca una conferma radar associata.</p>' : ''}
        <p class="ws-storm-note">Il percorso è una stima del moto, non una previsione di impatto o un’allerta. Crescita, dissoluzione e divisione delle celle possono modificarlo.</p>
    </article>`;
}

// --- hover (pattern popup di stations.js) ---------------------------------------

let hoverPopup = null;
let clickPopup = null;
function wireInteractions() {
    const map = state.map;
    hoverPopup = new maplibregl.Popup({
        closeButton: false, closeOnClick: false, offset: 12,
        className: 'wsv2-tooltip-shell ws-storm-popup-shell', maxWidth: '300px',
    });
    // Celle: popup informativo completo (port del popup v1, qui su hover).
    map.on('mousemove', LYR_CELLS, (e) => {
        const f = e.features && e.features[0];
        if (!f) return;
        map.getCanvas().style.cursor = 'pointer';
        const feats = (state.cells && state.cells.features) || [];
        const orig = feats[f.properties._idx];
        const p = (orig && orig.properties) || f.properties;
        hoverPopup.setLngLat(f.geometry.coordinates).setHTML(stormCellPopupHtml(p)).addTo(map);
    });
    map.on('mouseleave', LYR_CELLS, () => {
        map.getCanvas().style.cursor = '';
        hoverPopup.remove();
    });
    // Click/tap persistente: l'hover non esiste sui dispositivi touch.
    map.on('click', LYR_CELLS, (e) => {
        const f = e.features && e.features[0];
        if (!f) return;
        const feats = (state.cells && state.cells.features) || [];
        const orig = feats[f.properties._idx];
        const p = (orig && orig.properties) || f.properties;
        if (clickPopup) clickPopup.remove();
        clickPopup = new maplibregl.Popup({ closeButton: true, closeOnClick: true,
            offset: 12, className: 'ws-storm-popup-shell', maxWidth: '320px' })
            .setLngLat(f.geometry.coordinates).setHTML(stormCellPopupHtml(p)).addTo(map);
    });
    // Punti previsti: tooltip "+X′" / "posizione +X′" (v1 bindTooltip su hover).
    map.on('mousemove', LYR_POINTS, (e) => {
        const f = e.features && e.features[0];
        if (!f || !f.properties.lbl) return;
        map.getCanvas().style.cursor = 'pointer';
        hoverPopup.setLngLat(f.geometry.coordinates)
            .setHTML(`<div class="ws-storm-lead-tooltip">${esc(f.properties.lbl)}</div>`)
            .addTo(map);
    });
    map.on('mouseleave', LYR_POINTS, () => {
        map.getCanvas().style.cursor = '';
        hoverPopup.remove();
    });
}

// --- fetch (port di loadTemporaliLayer) ------------------------------------------

async function fetchCollection(url) {
    const response = await fetch(url, { cache: 'no-store', signal: AbortSignal.timeout(15000) });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'WSNOW_SOURCE_ERROR');
    return data;
}

async function loadData() {
    if (state._fetching) return;
    const generation = state.generation;
    state._fetching = true;
    state.fetchingGeneration = generation;
    try {
        const results = await Promise.allSettled([
            fetchCollection(WS_NATIONAL_API),
            fetchCollection(`${WS_LIGHTNING_API}?resource=storm-cells&area=italy&status=active&since_minutes=45`),
            fetchCollection(`${WS_LIGHTNING_API}?resource=nowcast&area=italy`),
        ]);
        if (!state.enabled || generation !== state.generation) return;
        const national = results[0].status === 'fulfilled' ? results[0].value : null;
        const electrical = results[1].status === 'fulfilled' && results[1].value?.type === 'FeatureCollection' ? results[1].value : null;
        const electricalNowcast = results[2].status === 'fulfilled' && results[2].value?.type === 'FeatureCollection' ? results[2].value : null;
        const radarCells = national?.cells?.type === 'FeatureCollection' ? national.cells : null;
        const radarNowcast = national?.nowcast?.type === 'FeatureCollection' ? national.nowcast : null;
        if (!radarCells && !electrical) throw new Error('WSNOW_ALL_SOURCES_UNAVAILABLE');
        state.nationalStatus = national?.status || null;
        state.sources = { radar: !!radarCells, electrical: !!electrical };
        state.cells = mergeNationalCells(radarCells, electrical);
        const canonicalIds = new Set(state.cells.features.filter(f => f.properties._track_source === 'lightning').map(f => String(f.properties.id)));
        const radarBase = collectionBaseTime(radarNowcast) ?? parseObservationTime(national?.status?.base_time_utc);
        const electricalBase = collectionBaseTime(electricalNowcast);
        const recentValidPath = f => {
            const base = parseObservationTime(f.properties?.base_time_utc);
            if (base === null || Date.now() - base > MAX_FORECAST_AGE_MS || base > Date.now() + 60000
                || f.geometry?.type !== 'LineString' || !Array.isArray(f.geometry.coordinates)
                || f.geometry.coordinates.length < 2 || !f.geometry.coordinates.every(c => Array.isArray(c) && c.length >= 2 && c.slice(0, 2).every(Number.isFinite))) return false;
            return (f.properties?.forecast_points || []).some(pt => pt.prospective !== false
                && Number.isFinite(Number(pt.lead_minutes)) && Number(pt.lead_minutes) > 0
                && Array.isArray(pt.coordinates) && pt.coordinates.length >= 2 && pt.coordinates.slice(0, 2).every(Number.isFinite)
                && base + Number(pt.lead_minutes) * 60000 > Date.now());
        };
        const radarPaths = (radarNowcast?.features || []).map(f => ({ ...f, properties: { ...f.properties,
            base_time_utc: f.properties?.base_time_utc || radarNowcast?.metadata?.base_time_utc || national?.status?.base_time_utc,
            _trajectory_source: 'national-radar',
        } })).filter(recentValidPath);
        const radarPathIds = new Set(radarPaths.map(f => String(f.properties?.cell_id ?? f.properties?.storm_cell_id ?? f.properties?.id)));
        const associatedRadar = new Map(state.cells.features.flatMap(f => (f.properties.lightning_cell_ids || []).map(id => [String(id), f.properties.id])));
        const electricalCandidates = (electricalNowcast?.features || []).map(f => ({ ...f, properties: { ...f.properties,
            base_time_utc: f.properties?.base_time_utc || f.properties?.run_time_utc || electricalNowcast?.metadata?.base_time_utc,
            _trajectory_source: 'electrical-tracking',
        } })).filter(recentValidPath).filter(f => {
            const id = String(f.properties?.cell_id ?? f.properties?.storm_cell_id ?? f.properties?.id);
            const radarId = associatedRadar.get(id);
            // Prefer the canonical radar path only when it actually exists.
            // A stationary/unsupported radar cell must not hide a fresh electric path.
            if (radarId !== undefined && !radarPathIds.has(String(radarId))) {
                f.properties._canonical_cell_id = radarId;
                return true;
            }
            return canonicalIds.has(id);
        });
        const electricalPathByCell = new Map();
        for (const path of electricalCandidates) {
            const key = String(path.properties._canonical_cell_id ?? path.properties.cell_id ?? path.properties.storm_cell_id ?? path.properties.id);
            const previous = electricalPathByCell.get(key);
            if (!previous || parseObservationTime(path.properties.base_time_utc) > parseObservationTime(previous.properties.base_time_utc)) electricalPathByCell.set(key, path);
        }
        const electricalPaths = [...electricalPathByCell.values()];
        state.nowcast = { type: 'FeatureCollection', features: [...radarPaths, ...electricalPaths] };
        const clocks = [radarBase, electricalBase, ...state.cells.features.map(f => parseObservationTime(f.properties.last_seen_at_utc))].filter(Number.isFinite);
        state.baseTimeMs = clocks.length ? Math.max(...clocks) : null;
        if (Number.isFinite(state.baseTimeMs)) NowcastTimeline.setDataClock('storms', state.baseTimeMs);
        else NowcastTimeline.clearDataClock('storms');
        const validForecasts = { features: state.nowcast.features.filter(f => {
            const base = parseObservationTime(f.properties.base_time_utc);
            return base !== null && Date.now() - base <= MAX_FORECAST_AGE_MS;
        }) };
        const lastForecastTime = Math.max(0, ...validForecasts.features.flatMap(f => (f.properties.forecast_points || [])
            .filter(pt => pt.prospective !== false).map(pt => (parseObservationTime(f.properties.base_time_utc) || 0) + (Number(pt.lead_minutes) || 0) * 60000)));
        const horizon = Number.isFinite(state.baseTimeMs) ? Math.max(0, Math.floor((lastForecastTime - state.baseTimeMs) / (5 * 60000)) * 5) : 0;
        NowcastTimeline.setForecastHorizon?.('storms', horizon);
        if (ensureLayers()) { renderCells(); drawPath(); }
        const feats = state.cells.features;
        const confirmed = feats.filter(f => f.properties.radar_confirmed === true || f.properties.evidence_class === 'radar_confirmed').length;
        const electricalCount = feats.filter(f => f.properties._track_source === 'lightning' && (f.properties.has_lightning || f.properties.detection_count)).length;
        const candidates = feats.length - confirmed - electricalCount;
        const summary = [];
        if (confirmed) summary.push(`${confirmed} ${confirmed === 1 ? 'cella radar' : 'celle radar'}`);
        if (electricalCount) summary.push(`${electricalCount} ${electricalCount === 1 ? 'cella elettrica' : 'celle elettriche'}`);
        if (candidates > 0) summary.push(`${candidates} in sviluppo`);
        const stale = national?.status?.stale === true;
        const degraded = national?.status?.degraded === true || (!horizon && feats.length > 0);
        const warnings = [];
        if (!radarCells) warnings.push('Radar nazionale non disponibile: solo attività elettrica');
        if (!electrical) warnings.push('Rete fulmini non disponibile: solo celle radar');
        if (national?.status?.warning) warnings.push(national.status.warning);
        emitStatus(`<strong>WS Now · Italia.</strong> ${summary.length ? summary.join(' · ') : 'nessuna cella rilevata'}.
            ${horizon ? `Percorsi stimati fino a +${horizon} min dal dato.` : 'Nessun percorso attendibile disponibile.'}
            ${warnings.map(esc).join(' · ')}`, {
            available: true, stale, count: feats.length, radarCount: confirmed,
            electricalCount, baseTimeMs: state.baseTimeMs, horizonMinutes: horizon, forecastUntilMs: lastForecastTime || null,
            warning: warnings.join(' · '), partial: !radarCells || !electrical || degraded,
            source: 'Radar DPC · Blitzortung', coverage: 'Italia e isole',
        });
        NowcastTimeline.notifyDataUpdated();
    } catch (err) {
        if (!state.enabled || generation !== state.generation) return;
        console.warn('[wsv2] temporali: fetch fallita', err);
        // Cached observations age out and cached forecasts expire independently.
        if (!Number.isFinite(state.baseTimeMs) || Date.now() - state.baseTimeMs > MAX_FORECAST_AGE_MS) NowcastTimeline.setForecastHorizon?.('storms', 0);
        if (ensureLayers()) { renderCells(); drawPath(); }
        emitStatus('<strong>WS Now · Italia.</strong> Fonti temporaneamente non disponibili. Riprovo automaticamente.',
            { available: false, stale: true, baseTimeMs: state.baseTimeMs, source: 'Radar DPC · Blitzortung' });
    } finally {
        state._fetching = false;
        if (state.enabled && generation !== state.generation) loadData();
    }
}

// --- API pubblica -----------------------------------------------------------------

export function initStorms(map, { beforeId } = {}) {
    state.map = map;
    state.beforeId = beforeId;
    wireInteractions();
}

// Attiva/disattiva layer + auto-refresh 2′ (port di activate/deactivateTemporaliLayer).
export function setStormsEnabled(on) {
    if (!!on === state.enabled) return;
    state.enabled = !!on;
    state.generation++;
    if (state.enabled) {
        publishStormsLegend(true);
        state.leadMin = 0;
        emitStatus('<strong>WS Now · Italia.</strong> Verifica delle fonti in corso.', { loading: true });
        loadData();
        state._visibilityHandler = () => { if (!document.hidden && state.enabled) loadData(); };
        document.addEventListener('visibilitychange', state._visibilityHandler);
        NowcastTimeline.register('storms', showAtTime);
        state.timer = setInterval(() => {
            if (!document.hidden && state.enabled) loadData();
        }, REFRESH_MS);
    } else {
        NowcastTimeline.unregister('storms');
        NowcastTimeline.clearDataClock('storms');
        NowcastTimeline.clearForecastHorizon?.('storms');
        if (state._visibilityHandler) document.removeEventListener('visibilitychange', state._visibilityHandler);
        state._visibilityHandler = null;
        if (state.timer) clearInterval(state.timer);
        state.timer = null;
        if (hoverPopup) hoverPopup.remove();
        if (clickPopup) { clickPopup.remove(); clickPopup = null; }
        state.cells = null;
        state.nowcast = null;
        state.leadMin = 0;
        state.baseTimeMs = null;
        state.targetTimeMs = null;
        clearData();
        publishStormsLegend(false);
        emitStatus('');
    }
}
